/**
 * CP Hub bell — Channel Partner-scoped notifications only (the ordinary
 * NavBar bell never shows them). Sits on the Forest Hub band, so the trigger
 * uses cream palette tokens; the panel uses semantic tokens.
 *
 * Items deep-link inside the Hub, never to `/service-request/$id`: the
 * request in a CP event belongs to the attributed customer.
 */
import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Bell, CheckCheck } from 'lucide-react';
import { cn } from '@finmin/ui';
import { useNotifications } from '@/features/notifications/notifications-context';
import { getEventMeta } from '@/features/notifications/event-meta';
import { formatRelativeTime } from '@/features/notifications/utils/format-relative-time';
import type { AppNotification } from '@/features/notifications/types';
import { hubDestination } from '../notification-links';

export function HubNotificationBell() {
  const navigate = useNavigate();
  const {
    channelPartnerNotifications: items,
    channelPartnerUnreadCount: unread,
    status,
    markAsRead,
    markAllAsRead,
  } = useNotifications();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  function openItem(n: AppNotification) {
    if (!n.read) markAsRead(n.id);
    setOpen(false);
    const dest = hubDestination(n);
    switch (dest.view) {
      case 'services':
        void navigate({
          to: '/channel-partner/hub/services',
          search: dest.customerId
            ? { customer: dest.customerId, customerName: n.data?.customer_name || undefined }
            : {},
        });
        break;
      case 'customers':
        void navigate({ to: '/channel-partner/hub/customers' });
        break;
      case 'profile':
        void navigate({ to: '/channel-partner/hub/profile' });
        break;
      default:
        void navigate({ to: '/channel-partner/hub' });
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread > 0 ? `Hub notifications, ${unread} unread` : 'Hub notifications'}
        aria-haspopup="true"
        aria-expanded={open}
        className="relative flex h-10 w-10 items-center justify-center rounded-md border border-cream/20 text-cream transition-colors hover:bg-cream/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Bell className="h-5 w-5" aria-hidden />
        {unread > 0 && (
          <span
            className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold leading-none text-accent-foreground"
            aria-hidden
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-hero/30 sm:hidden"
            aria-hidden
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className={cn(
              'fixed inset-x-2 top-24 z-50 overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg animate-slide-down-fade',
              'sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96',
            )}
          >
            <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
              <p className="text-sm font-semibold text-foreground">Hub notifications</p>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => markAllAsRead('channel_partner')}
                  className="inline-flex items-center gap-1 text-xs font-medium text-accent transition-opacity hover:opacity-80"
                >
                  <CheckCheck className="h-3.5 w-3.5" aria-hidden />
                  Mark all read
                </button>
              )}
            </div>

            <div className="max-h-[70vh] overflow-y-auto overscroll-contain sm:max-h-96">
              {status === 'error' ? (
                <div className="px-4 py-10 text-center">
                  <p className="text-sm font-medium text-foreground">Couldn&apos;t load notifications</p>
                  <p className="mt-1 text-xs text-muted-foreground">Please refresh the page.</p>
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
                  <Bell className="h-7 w-7 text-muted-foreground/50" aria-hidden />
                  <p className="text-sm text-muted-foreground">
                    Updates about your customers will appear here.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {items.map((n) => {
                    const { Icon } = getEventMeta(n.event);
                    return (
                      <li key={n.id}>
                        <button
                          type="button"
                          onClick={() => openItem(n)}
                          className={cn(
                            'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted',
                            !n.read && 'bg-accent/5',
                          )}
                        >
                          <span
                            className={cn(
                              'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                              n.read ? 'bg-muted text-muted-foreground' : 'bg-accent/15 text-accent',
                            )}
                          >
                            <Icon className="h-4 w-4" aria-hidden />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2">
                              <span className="truncate text-sm font-semibold text-foreground">
                                {n.title || 'Notification'}
                              </span>
                              {!n.read && (
                                <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-accent" aria-hidden />
                              )}
                            </span>
                            {n.body && (
                              <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
                                {n.body}
                              </span>
                            )}
                            <span className="mt-1 block text-[11px] text-muted-foreground/70">
                              {formatRelativeTime(n.createdAt)}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
